import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function MobilePaymentApp() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMode, setPaymentMode] = useState(false);
  const [transactionCode, setTransactionCode] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    loadCards();
  }, []);

  const loadCards = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/cards`, config);
      const activeCards = response.data.filter(card => card.status === 'active');
      setCards(activeCards);
      if (activeCards.length > 0) {
        setSelectedCard(activeCards[0]);
      }
    } catch (err) {
      console.error('Error loading cards:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateTransactionCode = () => {
    // Générer un code unique pour cette transaction
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `BKB-${selectedCard.id.substring(0, 8)}-${timestamp}-${random}`;
  };

  const activatePaymentMode = () => {
    const code = generateTransactionCode();
    setTransactionCode(code);
    setPaymentMode(true);
    
    // Désactiver automatiquement après 2 minutes
    setTimeout(() => {
      setPaymentMode(false);
      setTransactionCode('');
    }, 120000);
  };

  const cardTypeInfo = {
    visa: { name: 'Visa', color: 'from-blue-600 to-blue-800', icon: '💳' },
    mastercard: { name: 'Mastercard', color: 'from-orange-600 to-orange-800', icon: '💳' },
    cb: { name: 'CB', color: 'from-green-600 to-green-800', icon: '💳' }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-yellow-400 mx-auto mb-4"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">💳</div>
          <h2 className="text-2xl font-bold text-white mb-4">Aucune carte disponible</h2>
          <p className="text-gray-400 mb-6">Créez une carte virtuelle depuis votre espace client</p>
          <button
            onClick={() => navigate('/dashboard/cards')}
            className="px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-semibold hover:bg-yellow-400"
          >
            Créer une carte
          </button>
        </div>
      </div>
    );
  }

  if (paymentMode && selectedCard) {
    const cardInfo = cardTypeInfo[selectedCard.card_type];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex flex-col items-center justify-center p-4">
        {/* Header */}
        <div className="w-full max-w-md mb-8">
          <div className="flex items-center justify-between mb-4">
            <img src="/logo-bkb.jpg" alt="BKB" className="w-12 h-12 rounded-lg" />
            <button
              onClick={() => setPaymentMode(false)}
              className="text-white text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Payment QR Code */}
        <div className="bg-white rounded-3xl p-8 shadow-2xl mb-6 max-w-md w-full">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Prêt à payer</h2>
            <p className="text-gray-600">Scannez ce code sur la borne</p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-yellow-50 p-6 rounded-2xl mb-6">
            <div className="flex justify-center mb-4">
              <QRCodeSVG 
                value={transactionCode}
                size={220}
                level="H"
                includeMargin={true}
                className="rounded-xl"
              />
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-600 font-mono">{transactionCode}</p>
            </div>
          </div>

          {/* Card Info */}
          <div className={`bg-gradient-to-r ${cardInfo.color} rounded-xl p-4 text-white mb-4`}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm opacity-80">{cardInfo.name}</span>
              <span className="text-2xl">{cardInfo.icon}</span>
            </div>
            <p className="text-lg font-mono">•••• {selectedCard.card_number.slice(-4)}</p>
            <p className="text-sm mt-1">{selectedCard.cardholder_name}</p>
          </div>

          {/* Limits */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-gray-600 text-xs">Disponible aujourd'hui</p>
              <p className="font-bold text-green-600">
                {(selectedCard.daily_limit - selectedCard.daily_spent).toFixed(2)} €
              </p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-gray-600 text-xs">Disponible ce mois</p>
              <p className="font-bold text-green-600">
                {(selectedCard.monthly_limit - selectedCard.monthly_spent).toFixed(2)} €
              </p>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="text-center text-white max-w-md">
          <p className="text-sm opacity-80 mb-2">📱 Présentez ce QR code à la borne</p>
          <p className="text-xs opacity-60">Le code expire dans 2 minutes</p>
        </div>

        {/* Cancel Button */}
        <button
          onClick={() => setPaymentMode(false)}
          className="mt-6 px-8 py-3 bg-red-600 text-white rounded-full font-semibold hover:bg-red-700"
        >
          Annuler le paiement
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6 shadow-lg">
        <div className="flex items-center justify-between max-w-md mx-auto">
          <div className="flex items-center gap-3">
            <img src="/logo-bkb.jpg" alt="Bank Kreiz Breizh" className="w-12 h-12 rounded-lg" />
            <div>
              <h1 className="text-xl font-bold text-white">BKB Pay</h1>
              <p className="text-xs text-blue-200">Paiement sans contact</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-white text-2xl"
          >
            ☰
          </button>
        </div>
      </div>

      {/* Cards Carousel */}
      <div className="p-6 max-w-md mx-auto">
        <h2 className="text-white text-lg font-semibold mb-4">Mes cartes</h2>
        
        <div className="space-y-4 mb-8">
          {cards.map((card) => {
            const cardInfo = cardTypeInfo[card.card_type];
            const isSelected = selectedCard?.id === card.id;
            
            return (
              <div
                key={card.id}
                onClick={() => setSelectedCard(card)}
                className={`bg-gradient-to-r ${cardInfo.color} rounded-2xl p-6 text-white cursor-pointer transition-all transform ${
                  isSelected ? 'scale-105 ring-4 ring-yellow-400' : 'scale-100 opacity-80'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm opacity-80">{cardInfo.name}</p>
                    <p className="text-xl font-bold mt-1">{card.cardholder_name}</p>
                  </div>
                  <span className="text-3xl">{cardInfo.icon}</span>
                </div>

                <div className="mb-4">
                  <p className="text-2xl font-mono tracking-wider">
                    •••• •••• •••• {card.card_number.slice(-4)}
                  </p>
                </div>

                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-xs opacity-70">Expire le</p>
                    <p className="text-sm font-semibold">{card.expiry_date}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs opacity-70">Dispo. jour</p>
                    <p className="text-lg font-bold">
                      {(card.daily_limit - card.daily_spent).toFixed(0)} €
                    </p>
                  </div>
                </div>

                {isSelected && (
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <div className="flex items-center justify-center gap-2 text-yellow-300">
                      <span className="text-xl">✓</span>
                      <span className="text-sm font-semibold">Carte sélectionnée</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pay Button */}
        {selectedCard && (
          <button
            onClick={activatePaymentMode}
            className="w-full py-6 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 rounded-2xl font-bold text-xl shadow-2xl hover:from-yellow-300 hover:to-yellow-400 transition-all transform hover:scale-105 flex items-center justify-center gap-3"
          >
            <span className="text-3xl">📱</span>
            <span>Payer avec NFC</span>
          </button>
        )}

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/dashboard/cards')}
            className="py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20"
          >
            Gérer mes cartes
          </button>
          <button
            onClick={() => navigate('/dashboard/transactions')}
            className="py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20"
          >
            Historique
          </button>
        </div>
      </div>

      {/* Footer Info */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur p-4 border-t border-gray-800">
        <div className="max-w-md mx-auto text-center">
          <p className="text-white text-sm font-semibold mb-1">🔒 Paiement sécurisé</p>
          <p className="text-gray-400 text-xs">
            Vos données sont protégées par Bank Kreiz Breizh
          </p>
        </div>
      </div>
    </div>
  );
}

export default MobilePaymentApp;
